import classnames from 'classnames';
import * as React from 'react';
import { observer } from 'mobx-react';
import { Radio, FormControlLabel, RadioGroup, Tooltip, Icon } from '@material-ui/core';

import { AchInfoContainer } from '../../../../invoice/components/ach-info/ach-info';
import * as Utils from '../ach-utils';
import { ConfirmationModal } from './confirmation-modal';
import { toNumber } from 'src/util/formatters';
import { MatField } from 'src/core/components/mat-field';

import styles from './ach-info.less';
import { BankAccountHolderType, BankAccountType } from 'src/invoice/types/types';

interface Props {
    container: AchInfoContainer;
    amount?: number;
    containerStyles?: object;
    isInvoicePayment: boolean;
    redirectToPayPage?: () => void;
    isEmbedded?: boolean
}

export const AchInfo = observer<React.SFC<Props>>(
    ({ amount, containerStyles, container: { data }, isInvoicePayment, isEmbedded }) => {
        const maxAchPayment = 250000;
        const paymentAmount = amount || 0;
        return (
            <div style={containerStyles} className={isEmbedded ? undefined : classnames('card', styles.card)}>
                {paymentAmount > maxAchPayment ? (
                    <div className={styles.amountError}>
                        <Icon className={styles.warningIcon}>warning</Icon>
                        {Utils.getLimitText(isInvoicePayment, maxAchPayment)}
                    </div>
                ) : (
                    <>
                        {data.showAccountType && (<>
                        <div className={styles.fieldTitle}>
                            Bank Account Type
                        </div>
                        <RadioGroup
                            row
                            value={data.accountType.value}
                            onChange={data.changeAccountType}
                        >
                            <FormControlLabel
                                value={BankAccountType.Checking}
                                control={<Radio />}
                                label="Checking"
                            />
                            <FormControlLabel
                                value={BankAccountType.Savings}
                                control={<Radio />}
                                label="Savings"
                            />
                        </RadioGroup>
                        </>)}
                        {data.showAccountHolderType && (<>
                        <div className={styles.fieldTitle}>
                            Bank Account Holder Type
                            <Tooltip
                                enterTouchDelay={0}
                                classes={{
                                    tooltip: styles.infoTooltip,
                                }}
                                title="Different regulations apply to ACH bank transfers based on the source account. Selecting the correct type of account will help ensure that your payment is made without delay."
                            >
                                <Icon className={styles.infoIcon}>info</Icon>
                            </Tooltip>
                        </div>
                        <RadioGroup
                            row
                            value={data.accountHolderType.value}
                            onChange={data.changeAccountHolderType}
                        >
                            <FormControlLabel
                                value={BankAccountHolderType.Personal}
                                control={<Radio />}
                                label="Personal Account"
                            />
                            <FormControlLabel
                                value={BankAccountHolderType.Business}
                                control={<Radio />}
                                label="Business Account"
                            />
                        </RadioGroup>
                        </>)}
                        <div className={classnames('row', styles.row, styles.firstRow)}>
                            <div className={classnames('col', styles.col)}>
                                <MatField
                                    fullWidth
                                    errorColor
                                    fieldState={data.accountName}
                                    autoComplete="account-name"
                                    label="Name on Account"
                                />
                            </div>
                            <div className={classnames('col', styles.col)}>
                                <MatField
                                    fullWidth
                                    errorColor
                                    fieldState={data.routingNumber}
                                    formatter={toNumber}
                                    maxLength={9}
                                    autoComplete="routing-number"
                                    label="Routing Number"
                                />
                            </div>
                        </div>
                        <div className={classnames('row', styles.row, styles.firstRow)}>
                            <div className={classnames('col', styles.col)}>
                                <MatField
                                    fullWidth
                                    errorColor
                                    fieldState={data.accountNumber}
                                    formatter={toNumber}
                                    maxLength={26}
                                    autoComplete="account-number"
                                    label="Account Number"
                                />
                            </div>
                            <div className={classnames('col', styles.col)}>
                                <MatField
                                    fullWidth
                                    errorColor
                                    fieldState={data.confirmAccountNumber}
                                    formatter={toNumber}
                                    maxLength={26}
                                    autoComplete="confirm-account-number"
                                    label="Re-enter Account Number"
                                />
                            </div>
                        </div>
                        {data.modalIsOpen ? (
                            <ConfirmationModal
                                amount={paymentAmount}
                                companyName={data.companyName || ''}
                                companyPhoneNumber={data.phone || ''}
                                onClose={data.closeConfirmationModal}
                                onSubmit={data.handleSubmit}
                                accountNumber={data.accountNumber.value || ''}
                            />
                        ) : null}
                    </>
                )}
            </div>
        );
    }
);
